import React, { useState } from 'react';
import { ValidationIssue } from '../types/validation';
import './ValidationWarning.css';

interface ValidationWarningProps {
  issues: ValidationIssue[];
  onFix: () => void;
  onContinue: () => void;
  onViewDetails?: () => void;
  isFixing?: boolean;
}

const ValidationWarning: React.FC<ValidationWarningProps> = ({
  issues,
  onFix,
  onContinue,
  onViewDetails,
  isFixing = false
}) => {
  const [showDetails, setShowDetails] = useState(false);
  
  // Separate errors and warnings
  const errors = issues.filter(issue => issue.severity === 'error');
  const warnings = issues.filter(issue => issue.severity === 'warning');
  
  // Determine if we can auto-fix
  const canAutoFix = issues.some(issue => 
    ['incomplete_rename', 'missing_import'].includes(issue.type)
  );
  
  const getSeverityIcon = (severity: string) => {
    return severity === 'error' ? '❌' : '⚠️';
  };
  
  const getIssueTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      'syntax': 'Syntax Error',
      'incomplete_rename': 'Incomplete Rename',
      'missing_import': 'Missing Import',
      'type_error': 'Type Error',
      'type_safety': 'Type Safety',
      'dataframe_operation': 'DataFrame Operation',
      'file_path': 'File Path'
    };
    return labels[type] || type;
  };
  
  return (
    <div className="validation-warning">
      <div className="validation-header">
        <h3>
          {errors.length > 0 ? '❌ Issues Detected' : '⚠️ Warnings Detected'}
        </h3>
        <p className="validation-summary">
          {errors.length > 0 && `${errors.length} error${errors.length > 1 ? 's' : ''}`}
          {errors.length > 0 && warnings.length > 0 && ', '}
          {warnings.length > 0 && `${warnings.length} warning${warnings.length > 1 ? 's' : ''}`}
        </p>
      </div>
      
      <div className="validation-issues">
        {/* Show first few issues */}
        {issues.slice(0, showDetails ? undefined : 3).map((issue, index) => (
          <div key={index} className={`validation-issue ${issue.severity}`}>
            <div className="issue-header">
              <span className="issue-icon">{getSeverityIcon(issue.severity)}</span>
              <span className="issue-type">{getIssueTypeLabel(issue.type)}</span>
              {issue.line && <span className="issue-line">Line {issue.line}</span>}
              {issue.lines && issue.lines.length > 0 && (
                <span className="issue-line">Lines: {issue.lines.join(', ')}</span>
              )}
            </div>
            <div className="issue-message">{issue.message}</div>
            {issue.suggestion && (
              <div className="issue-suggestion">💡 {issue.suggestion}</div>
            )}
            {issue.code_context && Object.keys(issue.code_context).length > 0 && (
              <div className="issue-code-context">
                <div className="code-context-header">Code context:</div>
                <pre className="code-context-block">
                  {Object.entries(issue.code_context)
                    .sort(([a], [b]) => parseInt(a) - parseInt(b))
                    .map(([lineNum, code]) => {
                      const isHighlighted = issue.line === parseInt(lineNum) || 
                                          (issue.lines && issue.lines.includes(parseInt(lineNum)));
                      return (
                        <div key={lineNum} className={`code-line ${isHighlighted ? 'highlighted' : ''}`}>
                          <span className="line-number">{lineNum}:</span>
                          <span className="line-content">{code}</span>
                        </div>
                      );
                    })}
                </pre>
              </div>
            )}
          </div>
        ))}
        
        {/* Show more/less button */}
        {issues.length > 3 && (
          <button 
            className="show-more-button"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Show less' : `Show ${issues.length - 3} more...`}
          </button>
        )}
      </div>
      
      <div className="validation-actions">
        {canAutoFix && (
          <button 
            className="action-button fix-button"
            onClick={onFix}
            disabled={isFixing}
          >
            {isFixing ? (
              <>
                <span className="spinner">⚙️</span> Fixing...
              </>
            ) : (
              <>🔧 Fix with AI</>
            )}
          </button>
        )}
        
        <button 
          className="action-button continue-button"
          onClick={onContinue}
          disabled={isFixing}
        >
          {errors.length > 0 ? '⚡ Continue Anyway' : '✓ Continue'}
        </button>
        
        {onViewDetails && (
          <button 
            className="action-button details-button"
            onClick={onViewDetails}
          >
            👁️ View Script
          </button>
        )}
      </div>
    </div>
  );
};

export default ValidationWarning;